import BaseClient from './baseClient';
import {
  ProductWallGroupingResponse,
  KingpinReqParams,
} from '../types/newproductwall';

class NewProductWallClient extends BaseClient {
  private buildQueryUrl(
    baseUrl: string,
    params: KingpinReqParams,
    pathOverride?: string,
  ): string {
    const queryParams: Record<string, any> = {
      queryType: params.queryType || 'ALL',
      anchor: params.anchor || 0,
      count: params.count || 24,
      searchTerms: params.searchTerms,
      attributeIds: params.attributeIds,
      path: pathOverride ?? params.path,
      sortBy: params.sort,
      explain: params.explain,
      ruleStatus: params.ruleStatus,
      view: params.view,
      locationIds: params.locationIds,
      previewDate: params.previewDate,
      anonymousId: params.anonymousId,
      merchRules: params.merchRules,
      disableQueryFilter: params.disableQueryFilter,
      disableMlIntegration: params.disableMlIntegration,
      headerCallerId: params.headerCallerId,
      headerUserAgent: params.headerUserAgent,
    };

    // Filter out undefined/null values and convert to query string
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(
        ([_, v]) => v !== undefined && v !== null && v !== '',
      ),
    );

    // Convert to query string
    const queryString = Object.entries(filteredParams)
      .map(
        ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
      )
      .join('&');

    const options: string[] = [];
    if (params.options) {
      for (const option of Object.entries(params.options)) {
        if (option[1]) {
          options.push(`${option[0]}:${option[1]}`);
        }
      }
    }
    const optionsParam =
      options.length > 0 ? `&options=${options.join(',')}` : '';

    return `${baseUrl}?${queryString}${optionsParam}`;
  }

  async loadNextPage(
    params: KingpinReqParams,
  ): Promise<ProductWallGroupingResponse> {
    const baseUrl = `/proxy/discover/product_wall/v1/marketplace/${params.marketplace}/language/${params.language}/consumerChannelId/${params.channelId}`;

    let url;
    if (params.nextUrl) {
      url = `/proxy${params.nextUrl}`;
    } else {
      url = this.buildQueryUrl(baseUrl, params);
    }

    const res: ProductWallGroupingResponse = await this.fetch(url);

    // Handle redirect response for Nike.com channel
    if (res.analyzer?.action?.redirectUrl) {
      // Extract path from redirectUrl (e.g., /w/mens-nik1 from https://www.nike.com.cn/w/mens-nik1)
      const redirectUrl = res.analyzer.action.redirectUrl;
      const urlObj = new URL(redirectUrl);
      const redirectPath = urlObj.pathname; // e.g., /w/mens-nik1

      // Make another request with the redirect path
      const redirectFullUrl = this.buildQueryUrl(baseUrl, params, redirectPath);

      const redirectRes: ProductWallGroupingResponse = await this.fetch(
        redirectFullUrl,
      );
      return redirectRes;
    }

    if (!res.productGroupings || res.productGroupings.length === 0) {
      return {
        productGroupings: [],
        navigation: {
          facets: [],
          categories: [],
          categoriesBreadcrumbs: [],
          selectedFilters: [],
        },
        pages: {
          totalResources: 0,
          next: '',
          totalPages: 0,
          prev: '',
        },
      };
    }

    return res;
  }
}

export default new NewProductWallClient();
